import { useEffect, useId, useRef, useState } from 'react';
import { ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TASK_DESCRIPTION_MAX_LENGTH, TASK_TITLE_MAX_LENGTH } from '@/constants/tasks';
import { SECTION_LABEL } from '../taskTheme';

/**
 * The shell every editable text field in the head shares.
 *
 * The 9px padding and the 1px focus border are cancelled by a -10px margin,
 * so the field's text starts on the column's own left edge — flush with the id
 * chip and the Description label, which carry no padding — while the hover and
 * focus rectangle still stands clear of the glyphs. Padding alone indented the
 * title and the description 10px further in than everything above them. The
 * bleed lands inside the panel body's 18px gutter, so nothing overflows.
 */
const FIELD = 'mx-[-10px] w-[calc(100%_+_20px)] rounded-md border border-transparent bg-transparent px-[9px] outline-none hover:bg-[#FBFBFB] focus:border-[rgba(0,0,0,0.14)] focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,0,0,0.03)]';

/** The field's outline once what it holds cannot be saved; wins over hover and focus. */
const INVALID_FIELD = 'border-[#D14343] hover:bg-white focus:border-[#D14343] focus:shadow-[0_0_0_3px_rgba(209,67,67,0.08)]';

const formatCount = (count: number) => count.toLocaleString('en-US');

/** Why a field of `length` cannot be saved under `max`, or null when it can. */
const overLimit = (label: string, length: number, max: number) =>
  (length > max
    ? `${label} can be at most ${formatCount(max)} characters. This one is ${formatCount(length)}, so remove ${formatCount(length - max)}.`
    : null);

interface FieldErrorProps {
  id: string;
  message: string | null;
}

/** The line under a field saying why it will not save. Announced as it appears. */
const FieldError = ({ id, message }: FieldErrorProps) =>
  (message
    ? <p id={id} role="alert" className="mt-1 text-[12px] leading-[1.4] text-[#D14343]">{message}</p>
    : null);

interface TaskDetailHeadProps {
  taskKey: string;
  title: string;
  description: string | null;
  disabled?: boolean;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string | null) => void;
  /**
   * Set by a host that has tried to save with no title, so the field says why
   * rather than a toast. Only a new task can get here: an existing one puts its
   * old title back when the field is emptied.
   */
  titleRequired?: boolean;
  /** Told whenever either field starts or stops holding something that cannot be saved. */
  onValidityChange?: (valid: boolean) => void;
}

/**
 * The task's identity: its key, its title and its description.
 *
 * Both fields commit on blur rather than on every keystroke, so one edit is one
 * write. An empty title is refused because the column requires one — Escape
 * puts back what was there rather than leaving a field that cannot save.
 *
 * Anything longer than the server will hold is refused in place: the field is
 * marked, says by how much, and keeps the text so it can be trimmed. It is
 * never sent, because the server would refuse it and the edit would be lost.
 */
export const TaskDetailHead = ({
  taskKey, title, description, disabled = false, onTitleChange, onDescriptionChange,
  titleRequired = false, onValidityChange,
}: TaskDetailHeadProps) => {
  const [ draftTitle, setDraftTitle ] = useState(title);
  const [ draftDescription, setDraftDescription ] = useState(description ?? '');
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  // Per instance: the homepage can show this head in its aside and the chat
  // canvas at once, and a fixed id would point both at one error line.
  const fieldId = useId();
  const titleErrorId = `${fieldId}-title-error`;
  const descriptionErrorId = `${fieldId}-description-error`;
  const descriptionTextId = `${fieldId}-description-text`;
  // At rest a description is read, not edited: it shows as four lines and an
  // ellipsis, and becomes the field only when the reader asks to change it.
  const [ editingDescription, setEditingDescription ] = useState(false);

  useEffect(() => setDraftTitle(title), [ title ]);
  useEffect(() => setDraftDescription(description ?? ''), [ description ]);

  const titleError = overLimit('Titles', draftTitle.trim().length, TASK_TITLE_MAX_LENGTH)
    ?? (titleRequired && !draftTitle.trim() ? 'A task needs a title.' : null);
  const descriptionError = overLimit(
    'Descriptions', draftDescription.trim().length, TASK_DESCRIPTION_MAX_LENGTH,
  );
  const valid = !titleError && !descriptionError;
  // A description that cannot be saved stays open, so it can be trimmed.
  const showDescriptionField = editingDescription || !draftDescription || Boolean(descriptionError);

  useEffect(() => onValidityChange?.(valid), [ valid, onValidityChange ]);

  // Grown to its content rather than scrolled: a description is read far more
  // often than it is written, and a four-line box with a scrollbar hides most
  // of a long one.
  useEffect(() => {
    const field = descriptionRef.current;

    if (field) {
      field.style.height = 'auto';
      field.style.height = `${field.scrollHeight}px`;
    }
  }, [ draftDescription, showDescriptionField ]);

  // Opened on the reader's click, so the caret lands where they left off.
  useEffect(() => {
    const field = descriptionRef.current;

    if (editingDescription && field) {
      field.focus();
      field.setSelectionRange(field.value.length, field.value.length);
    }
  }, [ editingDescription ]);

  const commitTitle = () => {
    const next = draftTitle.trim();

    if (next.length > TASK_TITLE_MAX_LENGTH) {
      return;
    }

    if (!next) {
      setDraftTitle(title);

      return;
    }

    if (next !== title) {
      onTitleChange(next);
    }
  };

  const commitDescription = () => {
    setEditingDescription(false);

    const next = draftDescription.trim();

    if (next.length > TASK_DESCRIPTION_MAX_LENGTH) {
      return;
    }

    if (next !== (description ?? '')) {
      onDescriptionChange(next || null);
    }
  };

  return (
    <div>
      <div className="inline-flex items-center gap-1.5 rounded-[5px] bg-[#F1F0EC] px-[9px] py-1">
        <ListChecks className="size-4 shrink-0 text-[#9A9A9A]" aria-hidden />
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9A9A9A]">
          {taskKey}
        </span>
      </div>

      <input
        value={draftTitle}
        disabled={disabled}
        placeholder="Untitled"
        aria-label="Task title"
        // A long title ends in an ellipsis at rest, with the whole of it on
        // hover; focused, the field scrolls to whatever is being edited.
        title={draftTitle}
        aria-invalid={Boolean(titleError)}
        aria-describedby={titleError ? titleErrorId : undefined}
        className={cn(FIELD, 'mt-1.5 truncate font-fraunces text-base font-semibold tracking-[-0.02em] text-[#181818]', titleError && INVALID_FIELD)}
        onChange={(event) => setDraftTitle(event.target.value)}
        onBlur={commitTitle}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }

          if (event.key === 'Escape') {
            setDraftTitle(title);
          }
        }}
      />
      <FieldError id={titleErrorId} message={titleError} />

      <div className="mt-3.5">
        <div className={`${SECTION_LABEL} mb-1.5`}>Description</div>
        {showDescriptionField ? (
          <textarea
            ref={descriptionRef}
            value={draftDescription}
            disabled={disabled}
            rows={1}
            placeholder="Add a description"
            aria-label="Task description"
            aria-invalid={Boolean(descriptionError)}
            aria-describedby={descriptionError ? descriptionErrorId : undefined}
            // `break-words` states what a textarea's UA style already does, so a
            // pasted URL or an unbroken run of characters breaks inside the
            // column rather than widening it. Stated rather than inherited: the
            // auto-grow above measures `scrollHeight`, which only reports the
            // right height while the content wraps.
            className={cn(FIELD, 'resize-none break-words py-0.5 text-[13px] leading-[1.55] text-[#696969]', descriptionError && INVALID_FIELD)}
            onChange={(event) => setDraftDescription(event.target.value)}
            // However the field was reached — the read view's click, or
            // straight in while it is empty or over the limit — focus is what
            // holds it open. Without this, the first keystroke into an empty
            // description, or trimming one back under the limit, swapped the
            // focused field for the read view and the edit was never saved.
            onFocus={() => setEditingDescription(true)}
            onBlur={commitDescription}
          />
        ) : (
          // The same box and type as the field, so switching between the two
          // moves nothing but the lines past the fourth.
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label="Edit description"
            aria-describedby={descriptionTextId}
            aria-disabled={disabled || undefined}
            className={cn(FIELD, 'cursor-text py-0.5 text-left text-[13px] leading-[1.55] text-[#696969]')}
            onClick={() => !disabled && setEditingDescription(true)}
            onKeyDown={(event) => {
              if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                setEditingDescription(true);
              }
            }}
          >
            <p id={descriptionTextId} className="line-clamp-4 whitespace-pre-wrap break-words">
              {draftDescription}
            </p>
          </div>
        )}
        <FieldError id={descriptionErrorId} message={descriptionError} />
      </div>
    </div>
  );
};
